/**
 * PhasesPanel — Affiché en haut de la page « Phases & Atouts ».
 *
 * 08/02/2026 — Refonte phases (Q2). Synthèse pédagogique :
 *   1. Roadmap des 4 phases du jeu, déclencheurs et débloquages.
 *   2. Tableau des « unlocks » : quel support recrute quelle fonctionnalité.
 *
 * Localisé FR/EN via useTranslation (08/02/2026 v2).
 */
import React from 'react';
import { Lock, CheckCircle2, Circle, Sparkles, Truck, Building2, Globe2, ChevronRight } from 'lucide-react';
import { SUPPORT_ROLES } from '../data/supportRoles';
import { useTranslation } from '../i18n';

const PHASE_DEFS = [
  {
    id: 1, labelKey: 'phase_1_label', Icon: Truck, color: 'sky',
    triggerKeys: ['phase_1_trigger_1'],
    unlockKeys: ['phase_1_unlock_1', 'phase_1_unlock_2', 'phase_1_unlock_3'],
  },
  {
    id: 2, labelKey: 'phase_2_label', Icon: Building2, color: 'amber',
    triggerKeys: ['phase_2_trigger_1'],
    unlockKeys: ['phase_2_unlock_1', 'phase_2_unlock_2', 'phase_2_unlock_3'],
  },
  {
    id: 3, labelKey: 'phase_3_label', Icon: Building2, color: 'amber',
    triggerKeys: ['phase_3_trigger_1', 'phase_3_trigger_2', 'phase_3_trigger_3'],
    unlockKeys: ['phase_3_unlock_1', 'phase_3_unlock_2', 'phase_3_unlock_3', 'phase_3_unlock_4'],
  },
  {
    id: 4, labelKey: 'phase_4_label', Icon: Globe2, color: 'emerald',
    triggerKeys: ['phase_4_trigger_1', 'phase_4_trigger_2'],
    unlockKeys: ['phase_4_unlock_1', 'phase_4_unlock_2', 'phase_4_unlock_3', 'phase_4_unlock_4', 'phase_4_unlock_5'],
  },
];

function getColorClasses(color, isCurrent, isReached) {
  const palette = {
    sky:     { bg: 'bg-sky-500/10',     border: 'border-sky-400/40',   text: 'text-sky-200',     accent: 'text-sky-300' },
    amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-400/40', text: 'text-amber-200',   accent: 'text-amber-300' },
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-400/40', text: 'text-emerald-200', accent: 'text-emerald-300' },
  };
  const base = palette[color] || palette.sky;
  if (isCurrent) {
    return { ...base, border: base.border.replace('/40', '/80'), bg: base.bg.replace('/10', '/20') };
  }
  if (!isReached) {
    return { bg: 'bg-ink-800', border: 'border-ink-600', text: 'text-slate-400', accent: 'text-slate-500' };
  }
  return base;
}

function PhaseCard({ def, currentPhase, t }) {
  const isReached = currentPhase >= def.id;
  const isCurrent = currentPhase === def.id;
  const cls = getColorClasses(def.color, isCurrent, isReached);
  const StatusIcon = isCurrent ? Sparkles : isReached ? CheckCircle2 : Lock;
  const Icon = def.Icon;
  const ariaLabel = isCurrent ? t('phases_panel_aria_current')
    : isReached ? t('phases_panel_aria_reached')
    : t('phases_panel_aria_locked');
  return (
    <div
      className={`rounded-xl border-2 ${cls.border} ${cls.bg} p-3 ${isCurrent ? 'shadow-lg ring-2 ring-amber-400/30' : ''}`}
      data-testid={`phases-panel-card-${def.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className={`flex items-center gap-2 ${cls.accent}`}>
          <Icon className="w-4 h-4" />
          <h3 className="text-sm font-bold">{t(def.labelKey)}</h3>
        </div>
        <StatusIcon className={`w-4 h-4 ${cls.accent}`} aria-label={ariaLabel} />
      </div>
      <div className="space-y-2">
        <div>
          <p className={`text-[10px] uppercase tracking-wider font-mono ${cls.accent} mb-0.5`}>{t('phases_panel_trigger_label')}</p>
          <ul className={`text-[11px] ${cls.text} space-y-0.5 list-disc list-inside`}>
            {def.triggerKeys.map((k) => <li key={k}>{t(k)}</li>)}
          </ul>
        </div>
        <div>
          <p className={`text-[10px] uppercase tracking-wider font-mono ${cls.accent} mb-0.5`}>{t('phases_panel_unlocks_label')}</p>
          <ul className={`text-[11px] ${cls.text} space-y-0.5 list-disc list-inside`}>
            {def.unlockKeys.map((k) => <li key={k}>{t(k)}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

const FEATURE_FLAGS = [
  { flag: 'fiscal_simulator',     labelKey: 'feature_label_fiscal_simulator' },
  { flag: 'audit_fiscal',         labelKey: 'feature_label_audit_fiscal' },
  { flag: 'hr_dashboard',         labelKey: 'feature_label_hr_dashboard' },
  { flag: 'market_intel',         labelKey: 'feature_label_market_intel' },
  { flag: 'salons',               labelKey: 'feature_label_salons' },
  { flag: 'grands_comptes',       labelKey: 'feature_label_grands_comptes' },
  { flag: 'contrats_cadres',      labelKey: 'feature_label_contrats_cadres' },
  { flag: 'crisis_comm',          labelKey: 'feature_label_crisis_comm' },
  { flag: 'negociation_taux',     labelKey: 'feature_label_negociation_taux' },
  { flag: 'diag_preventif',       labelKey: 'feature_label_diag_preventif' },
  { flag: 'tax_auto_provisioning', labelKey: 'feature_label_tax_auto_provisioning' },
];

function FeatureUnlocksTable({ hiredSupport, t }) {
  const rows = [];
  FEATURE_FLAGS.forEach(({ flag, labelKey }) => {
    const providers = SUPPORT_ROLES.filter(r => Array.isArray(r.unlocks) && r.unlocks.includes(flag));
    if (providers.length === 0) return;
    const isUnlocked = providers.some(r => !!hiredSupport[r.id]);
    rows.push({ flag, label: t(labelKey), providers, isUnlocked });
  });
  rows.sort((a, b) => Number(a.isUnlocked) - Number(b.isUnlocked));

  return (
    <div className="rounded-xl border border-ink-600 bg-ink-800/50 p-3" data-testid="phases-panel-unlocks">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-amber-300" />
        <h3 className="text-sm font-bold text-foreground">{t('phases_panel_features_title')}</h3>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">{t('phases_panel_features_intro')}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-ink-600">
              <th className="text-left py-1 pr-3">{t('phases_panel_th_state')}</th>
              <th className="text-left py-1 pr-3">{t('phases_panel_th_feature')}</th>
              <th className="text-left py-1">{t('phases_panel_th_recruit')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.flag} className="border-b border-ink-700/60" data-testid={`phases-panel-unlock-${row.flag}`}>
                <td className="py-2 pr-3">
                  {row.isUnlocked
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" aria-label={t('phases_panel_unlocked')} />
                    : <Circle className="w-3.5 h-3.5 text-slate-500" aria-label={t('phases_panel_to_unlock')} />}
                </td>
                <td className={`py-2 pr-3 ${row.isUnlocked ? 'text-emerald-200' : 'text-slate-300'}`}>
                  {row.label}
                </td>
                <td className="py-2 text-slate-400">
                  {row.providers.map((p, i) => (
                    <span key={p.id} className="inline-flex items-center">
                      {i > 0 && <span className="mx-1 text-slate-600">{t('phases_panel_or')}</span>}
                      <span className={hiredSupport[p.id] ? 'text-emerald-200 font-bold' : 'text-slate-300'}>
                        {p.name}
                      </span>
                      <span className="ml-1 text-[10px] text-slate-500">({p.familyId})</span>
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PhasesPanel({ currentPhase = 1, hiredSupport = {} }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-3" data-testid="phases-panel">
      <div className="flex items-center gap-2">
        <ChevronRight className="w-4 h-4 text-amber-300" />
        <h2 className="text-base font-bold text-foreground">{t('phases_panel_progress_title')}</h2>
        <span className="text-[10px] uppercase tracking-wider font-mono text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-full px-2 py-0.5">
          {t('phases_panel_current_label')} : {currentPhase}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {PHASE_DEFS.map(def => (
          <PhaseCard key={def.id} def={def} currentPhase={currentPhase} t={t} />
        ))}
      </div>
      <FeatureUnlocksTable hiredSupport={hiredSupport} t={t} />
    </section>
  );
}
